import { useState, useCallback, useRef, useEffect } from 'react';
import type { LearningLanguage } from '../types';
import type { SavedTopic } from '../utils/topics';
import {
  getActiveTopicValues,
  normalizeSavedTopics,
  normalizeTopicValue,
  upsertSavedTopic,
} from '../utils/topics';

const SETTINGS_KEY = 'appSettings';
const DEBOUNCE_MS = 1000;

export interface PersistedSettings {
  learningLanguage: LearningLanguage;
  verbMode: boolean;
  topicEnabled: boolean;
  deselectedWords: string[];
  deselectedVerbs: string[];
  sentenceCount: string;
  wordsPerSentence: string;
  tenses: { present: boolean; past: boolean; future: boolean };
  grammaticalCase: string;
  deselectedCategories: string[];
  savedTopics: SavedTopic[];
  rememberedTopic: string;
}

const DEFAULTS: PersistedSettings = {
  learningLanguage: 'pl',
  verbMode: false,
  topicEnabled: false,
  deselectedWords: [],
  deselectedVerbs: [],
  sentenceCount: '5',
  wordsPerSentence: '4-8',
  tenses: { present: true, past: false, future: false },
  grammaticalCase: '',
  deselectedCategories: [],
  savedTopics: [],
  rememberedTopic: '',
};

function normalizeGrammaticalCase(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function loadSettings(): PersistedSettings {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      const learningLanguage = parsed.learningLanguage === 'lt' ? 'lt' : 'pl';
      const rememberedTopic = normalizeTopicValue(parsed.rememberedTopic);
      const savedTopics = rememberedTopic
        ? upsertSavedTopic(normalizeSavedTopics(parsed.savedTopics), rememberedTopic)
        : normalizeSavedTopics(parsed.savedTopics);
      const activeTopics = getActiveTopicValues(savedTopics);
      const topicEnabled = typeof parsed.topicEnabled === 'boolean'
        ? parsed.topicEnabled
        : Boolean(rememberedTopic || activeTopics.length > 0);
      return {
        ...DEFAULTS,
        ...parsed,
        learningLanguage,
        topicEnabled,
        grammaticalCase: normalizeGrammaticalCase(parsed.grammaticalCase),
        savedTopics,
        rememberedTopic: activeTopics.some((value) => value === rememberedTopic) ? rememberedTopic : '',
      };
    }
  } catch (e) {
    console.warn('Failed to parse appSettings from localStorage', e);
  }
  return { ...DEFAULTS };
}

function saveSettings(settings: PersistedSettings): void {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  } catch (e) {
    console.warn('Failed to save appSettings to localStorage', e);
  }
}

export function usePersistedSettings() {
  const [settings, setSettings] = useState<PersistedSettings>(loadSettings);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestRef = useRef<PersistedSettings>(settings);

  // Flush pending save on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        saveSettings(latestRef.current);
      }
    };
  }, []);

  const updateSettings = useCallback((partial: Partial<PersistedSettings>) => {
    setSettings((prev) => {
      const next = { ...prev, ...partial };
      latestRef.current = next;
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        saveSettings(latestRef.current);
      }, DEBOUNCE_MS);
      return next;
    });
  }, []);

  return { settings, updateSettings };
}
